import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { FaTicketAlt, FaCheck, FaCopy, FaCalendarAlt, FaFilm, FaClock, FaSparkles } from "react-icons/fa";

export default function CouponsPage() {
  const [copied, setCopied] = useState(false);
  const couponCode = "CC300";

  useEffect(() => {
    document.title = "Carnival Castle Coupons & Exclusive Deals | Save ₹300";
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(couponCode).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        fallbackCopy();
      });
    } else {
      fallbackCopy();
    }
  };

  const fallbackCopy = () => {
    try {
      const el = document.createElement("textarea");
      el.value = couponCode;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };

  return (
    <div className="cc-coupons-page">
      <Header />

      <main className="cc-coupons-main">
        {/* ── Background Elements ── */}
        <div className="cc-coupons-bg-ambient" />
        <div className="cc-coupons-pattern-overlay" />

        <div className="container cc-coupons-container">
          {/* ── Centered Hero Header ── */}
          <div className="cc-coupons-hero text-center">
            <span className="cc-eyebrow">
              ✦ EXCLUSIVE DEALS ✦
            </span>
            <h1 className="cc-hero-title">Carnival Castle Coupons</h1>
            <p className="cc-hero-subtitle">
              Copy a code · Use at checkout · Save instantly
            </p>
          </div>

          {/* ── Centered Premium Coupon Card ── */}
          <div className="cc-coupon-wrapper">
            <div className="cc-coupon-card">
              {/* Ticket side notches */}
              <div className="cc-ticket-notch cc-notch-left" />
              <div className="cc-ticket-notch cc-notch-right" />

              {/* Top Banner Accent */}
              <div className="cc-card-top-bar" />

              {/* Card Main Body */}
              <div className="cc-card-body">
                <div className="cc-card-header-row">
                  <span className="cc-offer-type-badge">
                    FLAT DISCOUNT
                  </span>
                  <span className="cc-live-badge">
                    <span className="cc-live-pulse" /> ACTIVE NOW
                  </span>
                </div>

                <div className="cc-offer-amount-wrap">
                  <div className="cc-offer-amount">₹300 OFF</div>
                  <div className="cc-offer-title">Grab Offer</div>
                </div>

                <p className="cc-offer-desc">
                  Get ₹300 off on your Carnival Castle celebration booking.
                </p>

                {/* Validity & Applicability Pills */}
                <div className="cc-offer-meta-grid">
                  <div className="cc-meta-pill">
                    <span className="cc-meta-icon"><FaCalendarAlt /></span>
                    <span className="cc-meta-label">Validity:</span>
                    <span className="cc-meta-value">Valid Till — 31 Dec 2026</span>
                  </div>

                  <div className="cc-meta-pill">
                    <span className="cc-meta-icon"><FaFilm /></span>
                    <span className="cc-meta-label">Theaters:</span>
                    <span className="cc-meta-value">All Theaters</span>
                  </div>

                  <div className="cc-meta-pill">
                    <span className="cc-meta-icon"><FaClock /></span>
                    <span className="cc-meta-label">Slots:</span>
                    <span className="cc-meta-value">All Slots</span>
                  </div>
                </div>
              </div>

              {/* Ticket Dashed Separator */}
              <div className="cc-ticket-divider">
                <div className="cc-dashed-line" />
              </div>

              {/* Card Bottom: Code & COPY Action */}
              <div className="cc-card-footer">
                <div className="cc-code-box">
                  <span className="cc-code-label">Use Code:</span>
                  <span className="cc-code-string" id="coupon-code-text">{couponCode}</span>
                </div>

                <button
                  type="button"
                  className={`cc-copy-btn ${copied ? "copied" : ""}`}
                  onClick={handleCopy}
                  aria-label={copied ? "Code copied to clipboard" : `Copy coupon code ${couponCode}`}
                >
                  {copied ? (
                    <>
                      <FaCheck className="cc-copy-icon" /> COPIED ✓
                    </>
                  ) : (
                    <>
                      <FaCopy className="cc-copy-icon" /> COPY
                    </>
                  )}
                </button>
              </div>

              {/* Success Notification Feedback */}
              {copied && (
                <div className="cc-copy-feedback-toast" role="status">
                  ✓ Code <strong>{couponCode}</strong> copied to clipboard! Paste at checkout to save ₹300.
                </div>
              )}
            </div>

            {/* Direct Booking CTA */}
            <div className="cc-coupon-cta-wrap text-center">
              <Link to="/locations" className="cc-coupon-book-btn">
                <span>🎉 Book Your Surprise with {couponCode}</span>
                <span className="cc-btn-arrow">→</span>
              </Link>
            </div>
          </div>

          {/* ── How to Redeem Section ── */}
          <div className="cc-redeem-steps-section">
            <h3 className="cc-steps-title">How to Redeem in 3 Easy Steps</h3>
            <div className="cc-steps-grid">
              <div className="cc-step-item">
                <div className="cc-step-num">1</div>
                <div className="cc-step-content">
                  <h4 className="cc-step-heading">Copy Code</h4>
                  <p className="cc-step-text">Click <strong>COPY</strong> above to copy coupon code <strong>CC300</strong> to your clipboard.</p>
                </div>
              </div>

              <div className="cc-step-item">
                <div className="cc-step-num">2</div>
                <div className="cc-step-content">
                  <h4 className="cc-step-heading">Pick Your Theatre & Slot</h4>
                  <p className="cc-step-text">Select your preferred branch, private theatre, date, and celebration slot.</p>
                </div>
              </div>

              <div className="cc-step-item">
                <div className="cc-step-num">3</div>
                <div className="cc-step-content">
                  <h4 className="cc-step-heading">Apply &amp; Save ₹300</h4>
                  <p className="cc-step-text">Enter <strong>CC300</strong> in the coupon box during checkout to get an instant ₹300 discount.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* ── Scoped Page Styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,900;1,600&family=DM+Sans:wght@400;500;600;700;800&display=swap');

        .cc-coupons-page {
          background-color: #12011F;
          min-height: 100vh;
          color: #FAF8F5;
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          position: relative;
          overflow-x: hidden;
        }

        .cc-coupons-main {
          position: relative;
          padding: 56px 0 90px;
          min-height: calc(100vh - 140px);
        }

        /* Ambient Glow & Pattern */
        .cc-coupons-bg-ambient {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 900px;
          height: 600px;
          background: radial-gradient(circle at center, rgba(94, 14, 115, 0.45) 0%, rgba(201, 168, 76, 0.12) 40%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        .cc-coupons-pattern-overlay {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(201, 168, 76, 0.08) 1px, transparent 1px);
          background-size: 32px 32px;
          opacity: 0.7;
          pointer-events: none;
          z-index: 0;
        }

        .cc-coupons-container {
          position: relative;
          z-index: 1;
          max-width: 820px;
          margin: 0 auto;
          padding: 0 20px;
        }

        /* Hero Header */
        .cc-coupons-hero {
          margin-bottom: 40px;
        }

        .cc-eyebrow {
          display: inline-block;
          font-size: 11.5px;
          font-weight: 800;
          letter-spacing: 2.2px;
          color: #F5D87A;
          text-transform: uppercase;
          background: rgba(201, 168, 76, 0.14);
          border: 1px solid rgba(201, 168, 76, 0.35);
          padding: 6px 16px;
          border-radius: 40px;
          margin-bottom: 14px;
        }

        .cc-hero-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(28px, 4.5vw, 42px);
          font-weight: 800;
          color: #FFFFFF;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
          text-shadow: 0 4px 18px rgba(0, 0, 0, 0.4);
        }

        .cc-hero-subtitle {
          font-size: clamp(14px, 2vw, 16px);
          color: rgba(250, 248, 245, 0.75);
          font-weight: 500;
          letter-spacing: 0.3px;
          margin: 0 auto;
        }

        /* Coupon Card Container */
        .cc-coupon-wrapper {
          max-width: 540px;
          margin: 0 auto 56px;
        }

        .cc-coupon-card {
          position: relative;
          background: linear-gradient(145deg, #240838 0%, #1A0329 100%);
          border: 1.5px solid rgba(201, 168, 76, 0.45);
          border-radius: 20px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.55), 0 0 35px rgba(94, 14, 115, 0.3);
          overflow: hidden;
          transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
        }

        .cc-coupon-card:hover {
          transform: translateY(-3px);
          border-color: #F5D87A;
          box-shadow: 0 26px 60px rgba(0, 0, 0, 0.65), 0 0 45px rgba(201, 168, 76, 0.25);
        }

        /* Ticket Notches */
        .cc-ticket-notch {
          position: absolute;
          width: 28px;
          height: 28px;
          background-color: #12011F;
          border-radius: 50%;
          z-index: 4;
          bottom: 74px;
        }

        .cc-notch-left {
          left: -14px;
          border-right: 1.5px solid rgba(201, 168, 76, 0.45);
        }

        .cc-notch-right {
          right: -14px;
          border-left: 1.5px solid rgba(201, 168, 76, 0.45);
        }

        /* Top Bar Accent */
        .cc-card-top-bar {
          height: 4px;
          background: linear-gradient(90deg, #9A6E10 0%, #F5D87A 50%, #C9A84C 100%);
        }

        /* Card Body */
        .cc-card-body {
          padding: 26px 30px 22px;
        }

        .cc-card-header-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .cc-offer-type-badge {
          display: inline-block;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.2px;
          color: #F5D87A;
          background: rgba(201, 168, 76, 0.15);
          border: 1px solid rgba(201, 168, 76, 0.35);
          padding: 4px 12px;
          border-radius: 6px;
          text-transform: uppercase;
        }

        .cc-live-badge {
          font-size: 11px;
          font-weight: 700;
          color: #4ADE80;
          display: flex;
          align-items: center;
          gap: 6px;
          letter-spacing: 0.5px;
        }

        .cc-live-pulse {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #4ADE80;
          box-shadow: 0 0 8px #4ADE80;
          display: inline-block;
          animation: pulseGreen 2s infinite;
        }

        @keyframes pulseGreen {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }

        .cc-offer-amount-wrap {
          display: flex;
          align-items: baseline;
          gap: 12px;
          margin-bottom: 8px;
        }

        .cc-offer-amount {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(34px, 6vw, 44px);
          font-weight: 900;
          line-height: 1;
          color: #FAF8F5;
          background: linear-gradient(135deg, #FFFFFF 30%, #F5D87A 70%, #C9A84C 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .cc-offer-title {
          font-size: 17px;
          font-weight: 700;
          color: #F5D87A;
          letter-spacing: 0.2px;
        }

        .cc-offer-desc {
          font-size: 14px;
          color: rgba(250, 248, 245, 0.85);
          line-height: 1.45;
          margin-bottom: 20px;
        }

        /* Meta Pills Grid */
        .cc-offer-meta-grid {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .cc-meta-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 12.5px;
        }

        .cc-meta-icon {
          color: #C9A84C;
          font-size: 13px;
          display: flex;
          align-items: center;
        }

        .cc-meta-label {
          color: rgba(250, 248, 245, 0.6);
          font-weight: 500;
        }

        .cc-meta-value {
          color: #FAF8F5;
          font-weight: 700;
          margin-left: 2px;
        }

        /* Dashed Divider */
        .cc-ticket-divider {
          position: relative;
          padding: 0 24px;
        }

        .cc-dashed-line {
          border-top: 1.5px dashed rgba(201, 168, 76, 0.35);
          width: 100%;
        }

        /* Card Footer: Code & Button */
        .cc-card-footer {
          padding: 16px 26px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          background: rgba(0, 0, 0, 0.22);
        }

        .cc-code-box {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .cc-code-label {
          font-size: 12.5px;
          font-weight: 600;
          color: rgba(250, 248, 245, 0.7);
        }

        .cc-code-string {
          font-family: 'DM Sans', monospace, sans-serif;
          font-size: 17px;
          font-weight: 800;
          color: #F5D87A;
          letter-spacing: 1.5px;
          background: rgba(201, 168, 76, 0.15);
          border: 1.5px dashed rgba(201, 168, 76, 0.55);
          padding: 6px 14px;
          border-radius: 8px;
          user-select: all;
          box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.25);
        }

        .cc-copy-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          background: linear-gradient(135deg, #B8860B 0%, #C9A84C 50%, #F5D87A 100%);
          color: #170224;
          font-size: 13.5px;
          font-weight: 800;
          letter-spacing: 0.8px;
          padding: 10px 22px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          transition: all 0.22s ease;
          box-shadow: 0 4px 16px rgba(201, 168, 76, 0.35);
          min-width: 108px;
          text-align: center;
        }

        .cc-copy-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(201, 168, 76, 0.5);
          color: #170224;
        }

        .cc-copy-btn.copied {
          background: #22C55E;
          color: #FFFFFF;
          box-shadow: 0 4px 16px rgba(34, 197, 94, 0.45);
        }

        .cc-copy-icon {
          font-size: 13px;
        }

        /* Toast Feedback */
        .cc-copy-feedback-toast {
          padding: 10px 20px;
          background: rgba(34, 197, 94, 0.18);
          border-top: 1px solid rgba(34, 197, 94, 0.4);
          color: #86EFAC;
          font-size: 12px;
          font-weight: 600;
          text-align: center;
          animation: toastFadeIn 0.2s ease;
        }

        @keyframes toastFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Booking CTA */
        .cc-coupon-cta-wrap {
          margin-top: 24px;
        }

        .cc-coupon-book-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: linear-gradient(135deg, #7B1A94 0%, #5E0E73 100%);
          color: #FFFFFF;
          border: 1.5px solid rgba(201, 168, 76, 0.5);
          padding: 13px 28px;
          border-radius: 50px;
          font-size: 14.5px;
          font-weight: 700;
          text-decoration: none;
          box-shadow: 0 8px 24px rgba(94, 14, 115, 0.4);
          transition: all 0.25s ease;
        }

        .cc-coupon-book-btn:hover {
          background: linear-gradient(135deg, #911FAD 0%, #7B1A94 100%);
          border-color: #F5D87A;
          color: #FFFFFF;
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(94, 14, 115, 0.55);
        }

        .cc-btn-arrow {
          font-size: 16px;
          transition: transform 0.22s ease;
        }

        .cc-coupon-book-btn:hover .cc-btn-arrow {
          transform: translateX(4px);
        }

        /* 3-Step Redemption Section */
        .cc-redeem-steps-section {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(201, 168, 76, 0.2);
          border-radius: 18px;
          padding: 32px 32px 28px;
          margin-top: 20px;
        }

        .cc-steps-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 20px;
          font-weight: 700;
          color: #F5D87A;
          text-align: center;
          margin-bottom: 26px;
          letter-spacing: 0.2px;
        }

        .cc-steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .cc-step-item {
          display: flex;
          align-items: flex-start;
          gap: 14px;
        }

        .cc-step-num {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(201, 168, 76, 0.15);
          border: 1.5px solid #C9A84C;
          color: #F5D87A;
          font-weight: 800;
          font-size: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .cc-step-content {
          flex: 1;
        }

        .cc-step-heading {
          font-size: 14px;
          font-weight: 700;
          color: #FAF8F5;
          margin-bottom: 4px;
        }

        .cc-step-text {
          font-size: 12.5px;
          color: rgba(250, 248, 245, 0.7);
          line-height: 1.45;
          margin: 0;
        }

        /* ── Responsive Rules ── */
        @media (max-width: 768px) {
          .cc-coupons-main {
            padding: 36px 0 70px;
          }
          .cc-card-body {
            padding: 22px 20px 18px;
          }
          .cc-card-footer {
            padding: 14px 18px 18px;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .cc-code-box {
            justify-content: center;
          }
          .cc-copy-btn {
            width: 100%;
            padding: 12px;
          }
          .cc-steps-grid {
            grid-template-columns: 1fr;
            gap: 18px;
          }
          .cc-redeem-steps-section {
            padding: 24px 20px;
          }
        }

        @media (max-width: 480px) {
          .cc-coupons-container {
            padding: 0 14px;
          }
          .cc-offer-amount-wrap {
            flex-direction: column;
            gap: 2px;
          }
          .cc-coupon-book-btn {
            width: 100%;
            justify-content: center;
            font-size: 13.5px;
            padding: 12px 18px;
          }
        }
      `}</style>
    </div>
  );
}
